import { useState, useEffect, useCallback } from 'react';
import { AudioAnalysis, AudioAnalysisFilters, Statistics, FilteredStatistics } from '../types';
import apiService from '../services/api';

// Hook para carregar análises de áudio
export const useAudioAnalyses = (initialPage: number = 1, initialFilters: AudioAnalysisFilters = {}) => {
  const [analyses, setAnalyses] = useState<AudioAnalysis[]>([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<AudioAnalysisFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalyses = useCallback(async (page: number = currentPage, currentFilters: AudioAnalysisFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAudioAnalyses(page, 20, currentFilters);
      
      if (response.success) {
        setAnalyses(response.data);
        setCurrentPage(response.pagination.currentPage);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.totalItems);
      } else {
        throw new Error('Erro ao carregar análises');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar análises');
      console.error('Erro ao carregar análises:', err);
    } finally {
      setLoading(false);
    }
  }, []); // Removendo dependências circulares

  const applyFilters = useCallback((newFilters: AudioAnalysisFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    loadAnalyses(1, newFilters);
  }, [loadAnalyses]);

  const clearFilters = useCallback(() => {
    const emptyFilters: AudioAnalysisFilters = {};
    setFilters(emptyFilters);
    setCurrentPage(1);
    loadAnalyses(1, emptyFilters);
  }, [loadAnalyses]);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    loadAnalyses(page);
  }, [loadAnalyses]);

  const refresh = useCallback(() => {
    loadAnalyses(currentPage, filters);
  }, [loadAnalyses, currentPage, filters]);

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    loadAnalyses(1, initialFilters);
  }, [loadAnalyses]); // Dependência apenas do loadAnalyses

  return {
    analyses,
    currentPage,
    totalPages,
    totalItems,
    filters,
    loading,
    error,
    applyFilters,
    clearFilters,
    goToPage,
    refresh,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
};

// Hook para carregar estatísticas
export const useStatistics = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getStatistics();
      
      if (response.success) {
        setStatistics(response.data);
      } else {
        throw new Error('Erro ao carregar estatísticas');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar estatísticas');
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadStatistics();
  }, [loadStatistics]);

  // Carregar dados iniciais
  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  return {
    statistics,
    loading,
    error,
    refresh,
  };
};

// Hook para carregar estatísticas filtradas
export const useFilteredStatistics = (filters: AudioAnalysisFilters) => {
  const [statistics, setStatistics] = useState<FilteredStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFilteredStatistics = useCallback(async (currentFilters: AudioAnalysisFilters) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getFilteredStatistics(currentFilters);
      
      if (response.success) {
        setStatistics(response.data);
      } else {
        throw new Error('Erro ao carregar estatísticas filtradas');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar estatísticas filtradas');
      console.error('Erro ao carregar estatísticas filtradas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadFilteredStatistics(filters);
  }, [loadFilteredStatistics, filters]);

  // Carregar dados quando os filtros mudarem
  useEffect(() => {
    loadFilteredStatistics(filters);
  }, [loadFilteredStatistics, filters]);

  return {
    statistics,
    loading,
    error,
    refresh,
  };
};

// Hook para carregar uma análise específica
export const useAudioAnalysis = (id: number | null) => {
  const [analysis, setAnalysis] = useState<AudioAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalysis = useCallback(async (analysisId: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAudioAnalysisById(analysisId);
      
      if (response.success && response.data) {
        setAnalysis(response.data);
      } else {
        throw new Error('Análise não encontrada');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar análise');
      console.error('Erro ao carregar análise:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      loadAnalysis(id);
    }
  }, [id, loadAnalysis]);

  return {
    analysis,
    loading,
    error,
    reload: () => id && loadAnalysis(id),
  };
}; 